<template>
    <a :class="linkClasses" :href="href" :title="label">
        <svg
            class="vertical-text-link__border"
            aria-hidden="true"
            width="32"
            height="150"
            viewBox="0 0 32 150"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
        >
            <rect
                x="0.5"
                y="0.5"
                width="31"
                height="149"
                rx="15.5"
                stroke="currentColor"
                stroke-dasharray="6 6"
            />
        </svg>
        <span class="vertical-text-link__label">
            <span
                v-for="(characterItem, index) in labelCharacters"
                :key="`${characterItem.value}-${index}`"
                :class="[
                    'vertical-text-link__character',
                    {
                        'vertical-text-link__character--punctuation':
                            characterItem.isPunctuation,
                    },
                ]"
                v-text="characterItem.displayValue"
            ></span>
        </span>
    </a>
</template>

<script setup lang="ts">
    import { computed } from 'vue';

    // ----- props -----
    const props = withDefaults(
        defineProps<{
            href: string;
            label: string;
            variant?: string;
        }>(),
        {
            variant: 'warning',
        }
    );

    // ----- computed -----
    const linkClasses = computed(() => [
        'vertical-text-link',
        `vertical-text-link-${props.variant}`,
    ]);

    // ----- methods -----
    // 將文案拆成逐字節點，並保留原始文案供 key 與標點判斷使用
    const labelCharacters = computed(() =>
        Array.from(props.label).map((character) => ({
            value: character,
            displayValue: character === '？' ? '?' : character,
            isPunctuation: character === '？',
        }))
    );
</script>

<style lang="scss" scoped>
    @use 'sass:map';

    $vertical-text-link-config: (
        warning: (
            class: 'warning',
            color: $textColor888,
            bg: #fffdeb,
            border-color: #ffd633,
            interaction-color: #f9b207,
            interaction-bg: #fffdeb,
            interaction-border-color: #ffd633,
        ),
    );

    .vertical-text-link {
        position: relative;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        box-sizing: border-box;
        width: 32px;
        height: 150px;
        padding: 12px 4px;
        border: none;
        border-radius: 100px;
        text-decoration: none;
        cursor: pointer;

        &__border {
            position: absolute;
            inset: 0;
            pointer-events: none;
        }

        &__label {
            position: relative;
            width: 24px;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            font-family: Arial, 'PingFang TC', 'Microsoft JhengHei', sans-serif;
            font-size: 14px;
            font-weight: 400;
            line-height: 1.5;
            text-align: center;
            z-index: 1;
        }

        &__character {
            width: 24px;
            height: 21px;
            display: flex;
            align-items: center;
            justify-content: center;
            line-height: 21px;
            text-align: center;

            &--punctuation {
                font-family: Arial, sans-serif;
                transform: none;
            }
        }
    }

    @each $key, $value in $vertical-text-link-config {
        $class: map.get($value, class);
        $color: map.get($value, color);
        $bg: map.get($value, bg);
        $border-color: map.get($value, border-color);
        $interaction-color: map.get($value, interaction-color);
        $interaction-bg: map.get($value, interaction-bg);
        $interaction-border-color: map.get($value, interaction-border-color);

        .vertical-text-link-#{$class} {
            color: $color;
            background-color: $bg;

            .vertical-text-link__border {
                color: $border-color;
            }

            &:hover,
            &:focus-visible,
            &:active {
                color: $interaction-color;
                background-color: $interaction-bg;
                text-decoration: none;

                .vertical-text-link__border {
                    color: $interaction-border-color;
                }
            }
        }
    }
</style>
